import { Column } from '@access-erp/common';
import { CellClickedEvent, CellType, FilterValueTypes } from '@access-erp/ui-components';

import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { guidFilterOptions } from '@/components/common/EnquiriesScreen/utils/filters.config';
import { BundleOutput, BundleTypeEnum } from '@/graphql/contracts/_generated_/contracts';

export interface UseTransactionTableColumnsReturn {
  headerColumns: Column<unknown>[];
}

type DefaultColumnArgsType = {
  getContractLink: (contractId: string, searchString?: string) => string;
  goToContract: (contractLink: string) => void;
};

export const getHeaderColumns = ({
  getContractLink,
  goToContract,
}: DefaultColumnArgsType): Column<unknown>[] => [
  {
    name: 'contractNumber',
    caption: 'Contract Number',
    width: 200,
    cellType: CellType.String,
    defaultFilter: 1,
    cellRenderer: createLinkCellRenderer<BundleOutput>(({ data, searchString }) => {
      const contractId = data?.contractId ?? '';
      if (!contractId) {
        return '#';
      }
      return getContractLink(contractId, searchString);
    }),
    onCellClicked: (e: CellClickedEvent) => {
      if (shouldSkipCellClickNavigation(e)) {
        return;
      }
      const searchStringIfAny = e.context?.serverSide?.current?.search;
      const contractId = e.data.contractId ?? '';
      goToContract(getContractLink(contractId, searchStringIfAny));
    },
  },
  {
    name: 'description',
    caption: 'Description',
    width: 200,
    cellType: CellType.String,
    defaultFilter: 2,
  },
  {
    name: 'softwareCompany',
    caption: 'Software Company',
    width: 200,
    cellType: CellType.String,
    defaultFilter: 3,
  },
  {
    name: 'type',
    caption: 'Type',
    width: 150,
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: () => [
      { label: 'Contractual', value: BundleTypeEnum.Contractual },
      { label: 'Custom', value: BundleTypeEnum.Custom },
      { label: 'Suite', value: BundleTypeEnum.Suite }
    ],
    defaultFilter: 4,
  },
  {
    name: 'totalAnnualBillingValue',
    caption: 'Annual Billing Value',
    width: 200,
    cellStyle: { justifyContent: 'end' },
    cellType: CellType.String,
    cellFormatter: (params) => CellFormatters.decimal(params),
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    defaultFilter: 5,
  },
  {
    name: 'totalPredictedAnnualValue',
    caption: 'Predicted Annual Value',
    width: 200,
    cellStyle: { justifyContent: 'end' },
    cellType: CellType.String,
    cellFormatter: (params) => CellFormatters.decimal(params),
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    defaultFilter: 6,
  },
  {
    name: 'hideInvoiceLines',
    caption: 'Hide Invoice Lines',
    width: 200,
    cellType: CellType.Bool,
    defaultFilter: 7,
  },
  {
    name: 'hideInvoiceValues',
    caption: 'Hide Invoice Values',
    width: 160,
    cellType: CellType.Bool,
    defaultFilter: 8,
  },
  {
    name: 'currency',
    caption: 'Currency',
    width: 120,
    cellType: CellType.String,
    defaultFilter: 9,
  },
  {
    name: 'id',
    caption: 'ID',
    width: 270,
    cellType: CellType.String,
    defaultFilter: 10,
    hidden: true,
    filterType: FilterValueTypes.StringFilter,
    filterOptions: {
      availableOptions: guidFilterOptions,
    },
  },
];
